import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Crown, Sparkles, CreditCard, IndianRupee } from "lucide-react";

function loadScript(src) {
  return new Promise((resolve) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve(true);
    const s = document.createElement("script");
    s.src = src;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Billing() {
  const { user, refresh } = useAuth();
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [provider, setProvider] = useState("stripe");
  const [razorpay, setRazorpay] = useState({ enabled: false, key_id: "" });

  useEffect(() => {
    api.get("/analytics/summary").then((r) => setStats(r.data)).catch(() => {});
    api.get("/razorpay/config").then((r) => {
      setRazorpay(r.data);
      if (r.data.enabled) setProvider("razorpay");
    }).catch(() => {});
  }, []);

  const upgradeStripe = async () => {
    setBusy(true);
    try {
      const origin_url = window.location.origin;
      const { data } = await api.post("/subscription/checkout", { plan: "paid", origin_url });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const upgradeRazorpay = async () => {
    setBusy(true);
    try {
      const ok = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
      if (!ok) throw new Error("Razorpay SDK failed to load");
      const { data: order } = await api.post("/razorpay/order", { plan: "paid" });
      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "DmFlio",
        description: "DmFlio Pro — Monthly",
        order_id: order.order_id,
        prefill: { email: user.email, name: user.name },
        theme: { color: "#FF8866" },
        handler: async (res) => {
          try {
            await api.post("/razorpay/verify", {
              order_id: res.razorpay_order_id,
              payment_id: res.razorpay_payment_id,
              signature: res.razorpay_signature,
            });
            toast.success("Welcome to Pro!");
            await refresh();
          } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      };
      // eslint-disable-next-line no-undef
      const rz = new window.Razorpay(options);
      rz.open();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  const isPaid = user?.plan === "paid";

  return (
    <div className="space-y-6 max-w-5xl" data-testid="billing-page">
      <header>
        <span className="chip bg-butter">Billing</span>
        <h1 className="text-4xl md:text-5xl font-extrabold mt-3">Plan & Subscription</h1>
        <p className="text-muted-foreground font-medium mt-1">Upgrade for unlimited DMs and priority delivery.</p>
      </header>

      <div className="brutal p-6 bg-secondary flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider">Current plan</div>
          <div className="font-heading font-black text-3xl mt-1 flex items-center gap-2" data-testid="current-plan">
            {isPaid ? (<><Crown className="w-7 h-7" strokeWidth={2.5} /> Pro</>) : "Free"}
          </div>
          {!isPaid && stats && (
            <div className="text-sm font-semibold mt-1">{stats.monthly_used} / {stats.monthly_limit} DMs used this month</div>
          )}
        </div>
        {isPaid && <span className="chip bg-mint">Unlimited DMs</span>}
      </div>

      {!isPaid && (
        <div className="flex gap-2 flex-wrap" data-testid="provider-tabs">
          <button
            onClick={() => setProvider("stripe")}
            data-testid="provider-stripe"
            className={`inline-flex items-center gap-1.5 px-4 py-2 border-2 border-foreground rounded-md text-sm font-bold ${provider === "stripe" ? "bg-primary shadow-brutal-sm" : "bg-white"}`}
          >
            <CreditCard className="w-4 h-4" strokeWidth={2.5} /> Stripe (Card · USD)
          </button>
          <button
            onClick={() => razorpay.enabled && setProvider("razorpay")}
            disabled={!razorpay.enabled}
            data-testid="provider-razorpay"
            className={`inline-flex items-center gap-1.5 px-4 py-2 border-2 border-foreground rounded-md text-sm font-bold ${provider === "razorpay" ? "bg-primary shadow-brutal-sm" : "bg-white"} disabled:opacity-50`}
          >
            <IndianRupee className="w-4 h-4" strokeWidth={2.5} /> Razorpay (UPI/Card · INR)
            {!razorpay.enabled && <span className="chip bg-muted text-[10px] ml-1">setup needed</span>}
          </button>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-5">
        <div className={`brutal p-7 ${isPaid ? "bg-muted" : "bg-white"}`}>
          <h3 className="font-heading font-extrabold text-2xl">Free</h3>
          <div className="my-3">
            <span className="font-heading font-black text-5xl">₹0</span>
            <span className="text-muted-foreground font-semibold">/mo</span>
          </div>
          <ul className="space-y-2.5 text-sm font-semibold mb-6">
            {["40 DMs / month", "Unlimited rules", "Basic analytics"].map((t) => (
              <li key={t} className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5" strokeWidth={3} />{t}</li>
            ))}
          </ul>
          <button disabled className="w-full py-3 border-2 border-foreground rounded-md font-bold bg-muted opacity-60 cursor-not-allowed">
            {isPaid ? "Available on downgrade" : "Current plan"}
          </button>
        </div>

        <div className="brutal p-7 bg-primary relative">
          {!isPaid && <div className="absolute -top-3 right-6 chip bg-accent">Recommended</div>}
          <h3 className="font-heading font-extrabold text-2xl flex items-center gap-2">
            <Sparkles className="w-6 h-6" strokeWidth={2.5} /> Pro
          </h3>
          <div className="my-3">
            <span className="font-heading font-black text-5xl">₹199</span>
            <span className="font-semibold">/mo</span>
          </div>
          <ul className="space-y-2.5 text-sm font-semibold mb-6">
            {["Unlimited DMs", "Unlimited rules", "Priority delivery", "Full analytics", "Priority support"].map((t) => (
              <li key={t} className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5" strokeWidth={3} />{t}</li>
            ))}
          </ul>
          {isPaid ? (
            <button disabled className="w-full py-3 bg-foreground text-white border-2 border-foreground rounded-md font-bold">
              Current plan ✓
            </button>
          ) : provider === "razorpay" ? (
            <button onClick={upgradeRazorpay} disabled={busy} data-testid="upgrade-razorpay-btn" className="w-full py-3 bg-foreground text-white border-2 border-foreground rounded-md font-bold shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 transition-all disabled:opacity-50">
              {busy ? "Opening…" : "Pay ₹199 · Razorpay"}
            </button>
          ) : (
            <button onClick={upgradeStripe} disabled={busy} data-testid="upgrade-btn" className="w-full py-3 bg-foreground text-white border-2 border-foreground rounded-md font-bold shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 transition-all disabled:opacity-50">
              {busy ? "Redirecting…" : "Upgrade with Stripe"}
            </button>
          )}
        </div>
      </div>

      <p className="text-xs text-muted-foreground font-medium">
        By subscribing, you agree to our <a href="/terms" target="_blank" rel="noopener" className="underline">Terms of Service</a> and <a href="/privacy" target="_blank" rel="noopener" className="underline">Privacy Policy</a>.
      </p>
    </div>
  );
}
